import {
  EffortImpactBand,
  EffortImpactMeta,
  EffortImpactScale,
  Product,
  ProductAssessment,
  ProductDoc,
  ProductRoadmapData,
  SlackChannel
} from './product-roadmap-constants';

// This page's data is fetched at RUNTIME from product-brain's generated JSON in S3 (see
// useProductRoadmapData). TypeScript therefore guarantees nothing about it: `ProductRoadmapData`
// is an assertion about a payload the compiler never sees, produced by a Python generator in a
// different repo that can be changed and republished without this app rebuilding.
//
// That gap has bitten twice:
//   - `appPath` was declared `string[]` but the generator emitted a bare string from its first
//     commit. `ProductCard` called `product.appPath.map(...)`, which threw
//     "e.appPath.map is not a function" and replaced the whole card with "An unknown error has
//     occurred." It hit 48 of 72 products -- every one with a repo or Slack link, because
//     `hasLinks` gates that section on those too. Products with no links never reached the
//     `.map`, which made a systemic bug look product-specific.
//   - `intelligence` simply did not exist in data published before product-brain #57, which is
//     why that field is already optional in the interface.
//
// So: coerce here, once, at the boundary -- rather than scattering `Array.isArray` guards through
// the components, or trusting a type that describes a contract we cannot enforce. A malformed or
// missing field should degrade to an empty/blank rendering, never take down the card.
//
// Keep this in step with the `Product` interface: a new array field needs a line here too.

const asString = (v: unknown): string => (typeof v === 'string' ? v : '');

// Accepts an array, tolerates a bare string (the appPath bug's exact shape) by promoting it to a
// single-element array, and treats anything else -- null, undefined, a number, an object -- as
// empty. Non-string members are dropped rather than rendered as "[object Object]".
const asStringArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  if (typeof v === 'string' && v.length > 0) return [v];
  return [];
};

const asDocs = (v: unknown): ProductDoc[] => {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (d): d is ProductDoc =>
      typeof d === 'object' && d !== null && typeof (d as ProductDoc).title === 'string'
  );
};

// `null` is the documented "no channel" value; an object is only usable if it actually carries
// the two fields the card renders.
const asSlackChannel = (v: unknown): SlackChannel | null => {
  if (typeof v !== 'object' || v === null) return null;
  const c = v as Partial<SlackChannel>;
  return typeof c.name === 'string' && typeof c.id === 'string' ? { name: c.name, id: c.id } : null;
};

// A score is a whole number 1-10; anything else (missing, a string, 0, 11, 6.5) is not a score.
const asScore = (v: unknown): number | null =>
  typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 10 ? v : null;

// Both scores must be valid for a product to be plotted -- half an assessment is no assessment,
// and the page shows it as "not yet scored" rather than guessing the missing axis.
const asAssessment = (v: unknown): ProductAssessment | null => {
  if (typeof v !== 'object' || v === null) return null;
  const a = v as Partial<ProductAssessment>;
  const impact = asScore(a.impact);
  const effort = asScore(a.effort);
  if (impact === null || effort === null) return null;
  return {
    impact,
    effort,
    scoredBy: asString(a.scoredBy),
    date: asString(a.date),
    draft: a.draft === true
  };
};

// A scale is usable only with a definition and at least one band that has text; otherwise the
// card would render half-empty, so the whole scale is dropped.
const asScale = (v: unknown): EffortImpactScale | null => {
  if (typeof v !== 'object' || v === null) return null;
  const sc = v as Partial<EffortImpactScale>;
  const bands = (Array.isArray(sc.bands) ? sc.bands : [])
    .map((b: unknown) => {
      const band = (typeof b === 'object' && b !== null ? b : {}) as Partial<EffortImpactBand>;
      return { band: asString(band.band), lead: asString(band.lead), text: asString(band.text) };
    })
    .filter(b => b.band && b.text);
  const definition = asString(sc.definition);
  if (!definition || bands.length === 0) return null;
  return { definition, note: asString(sc.note), bands };
};

const asEffortImpactMeta = (v: unknown): EffortImpactMeta | undefined => {
  if (typeof v !== 'object' || v === null) return undefined;
  const m = v as Partial<EffortImpactMeta> & { scales?: Record<string, unknown> };
  const count = (n: unknown) => (typeof n === 'number' && n >= 0 ? Math.floor(n) : 0);
  const meta: EffortImpactMeta = {
    inScope: count(m.inScope),
    scored: count(m.scored),
    draft: m.draft === true
  };
  // Both scales or neither: the card explains the two axes together.
  const impact = asScale(m.scales?.impact);
  const effort = asScale(m.scales?.effort);
  if (impact && effort) {
    meta.scales = { impact, effort };
  }
  return meta;
};

export const normalizeProduct = (raw: unknown): Product => {
  const p = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<Product>;
  return {
    name: asString(p.name),
    kind: asString(p.kind),
    parent: asString(p.parent),
    scope: asString(p.scope),
    channel: asStringArray(p.channel),
    function: asStringArray(p.function),
    audience: asString(p.audience),
    intelligence: asStringArray(p.intelligence),
    desc: asString(p.desc),
    oneLiner: asString(p.oneLiner),
    life: asString(p.life),
    target: asString(p.target),
    owner: asString(p.owner),
    docs: asDocs(p.docs),
    docsStatus: asString(p.docsStatus),
    repoUrl: asString(p.repoUrl),
    appPath: asStringArray(p.appPath),
    slackChannel: asSlackChannel(p.slackChannel),
    assessment: asAssessment(p.assessment),
    assessable: p.assessable === true
  };
};

export const normalizeProductRoadmapData = (raw: unknown): ProductRoadmapData => {
  const d = (typeof raw === 'object' && raw !== null ? raw : {}) as Partial<ProductRoadmapData>;
  const meta = (typeof d.meta === 'object' && d.meta !== null ? d.meta : {}) as Partial<
    ProductRoadmapData['meta']
  >;
  return {
    meta: {
      asof: asString(meta.asof),
      version: asString(meta.version),
      effortImpact: asEffortImpactMeta(meta.effortImpact)
    },
    products: Array.isArray(d.products) ? d.products.map(normalizeProduct) : []
  };
};
